import React, { useState, useEffect, useRef } from 'react';
import { UseFormRegister, FieldErrors, UseFormTrigger, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ThemedDatePicker } from '../common/themed-datepicker.component';
import { ATTACHMENT_ACCEPT } from '../../utils/attachment.utils';

interface MaterialsToolsFormProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  trigger: UseFormTrigger<any>;
  setValue: UseFormSetValue<any>;
  isTaxFree: string;
  selectedFiles: File[];
  totalWithVat: string;
  fileError?: string;
  onFormChange: (field: string, value: any) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  formData?: any;
  existingFileName?: string;
  onDownloadExistingFile?: () => void;
  isDownloadingFile?: boolean;
}

const MaterialsToolsForm: React.FC<MaterialsToolsFormProps> = ({
  register,
  errors,
  trigger,
  setValue,
  isTaxFree,
  selectedFiles,
  totalWithVat,
  fileError,
  onFormChange,
  onFileUpload,
  onRemoveFile,
  formData,
  existingFileName,
  onDownloadExistingFile,
  isDownloadingFile
}) => {
  const { t } = useTranslation();

  // Dropdown states
  const [isTaxFreeDropdownOpen, setIsTaxFreeDropdownOpen] = useState(false);
  const [showOnInvoiceDropdownOpen, setShowOnInvoiceDropdownOpen] = useState(false);
  const [isPdfAttachedDropdownOpen, setIsPdfAttachedDropdownOpen] = useState(false);
  const [vatDropdownOpen, setVatDropdownOpen] = useState(false);

  // Refs for click outside
  const taxFreeDropdownRef = useRef<HTMLDivElement>(null);
  const showOnInvoiceDropdownRef = useRef<HTMLDivElement>(null);
  const isPdfAttachedDropdownRef = useRef<HTMLDivElement>(null);
  const vatDropdownRef = useRef<HTMLDivElement>(null);

  // Options
  const taxFreeOptions = [
    { value: '', label: t('SELECT_OPTION') },
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  const yesNoOptions = [
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  const vatOptions = [
    { value: '', label: t('SELECT_VAT') },
    { value: '0', label: t('VAT_0') },
    { value: '6', label: t('VAT_6') },
    { value: '12', label: t('VAT_12') },
    { value: '25', label: t('VAT_25') }
  ];

  // Helper functions
  const getTaxFreeLabel = (value: string) => {
    if (!value || value === '') return t('SELECT_OPTION');
    return value === 'yes' ? t('YES') : t('NO');
  };

  const getYesNoLabel = (value: string) => {
    return value === 'yes' ? t('YES') : t('NO');
  };

  const getVatLabel = (value: string) => {
    if (!value || value === '') return t('SELECT_VAT');
    const option = vatOptions.find(opt => opt.value === value);
    return option ? option.label : t('SELECT_VAT');
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (taxFreeDropdownRef.current && !taxFreeDropdownRef.current.contains(event.target as Node)) {
        setIsTaxFreeDropdownOpen(false);
      }
      if (showOnInvoiceDropdownRef.current && !showOnInvoiceDropdownRef.current.contains(event.target as Node)) {
        setShowOnInvoiceDropdownOpen(false);
      }
      if (isPdfAttachedDropdownRef.current && !isPdfAttachedDropdownRef.current.contains(event.target as Node)) {
        setIsPdfAttachedDropdownOpen(false);
      }
      if (vatDropdownRef.current && !vatDropdownRef.current.contains(event.target as Node)) {
        setVatDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* Row 1: Is Tax Free & Show on Invoice */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('IS_MATERIALS_TAX_FREE')}</label>
          <div ref={taxFreeDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isTaxFree', { required: t('PLEASE_SELECT_OPTION') })} />
            <div
              onClick={() => setIsTaxFreeDropdownOpen(!isTaxFreeDropdownOpen)}
              style={{
                height: '46px',
                border: errors.isTaxFree ? '1px solid #dc3545' : '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: (!isTaxFree || isTaxFree === '') ? '#68727d' : '#252525',
                boxShadow: isTaxFreeDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isTaxFreeDropdownOpen ? '1px solid #7c3aed' : 'none'
              }}
            >
              <span>{getTaxFreeLabel(isTaxFree)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isTaxFreeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isTaxFreeDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {taxFreeOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isTaxFree', option.value);
                      trigger('isTaxFree');
                      setIsTaxFreeDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: isTaxFree === option.value ? '#f4efff' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (isTaxFree !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4efff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isTaxFree !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.isTaxFree && (
            <div className="error-message">{errors.isTaxFree.message?.toString()}</div>
          )}
        </div>

        {/* Show on Invoice — always visible, regardless of the tax-free selection */}
        {(
          <div className="form-group">
            <label>{t('SHOW_ON_INVOICE_QUESTION')}</label>
            <div ref={showOnInvoiceDropdownRef} style={{ position: 'relative' }}>
              <input type="hidden" {...register('showOnInvoice')} />
              <div
                onClick={() => setShowOnInvoiceDropdownOpen(!showOnInvoiceDropdownOpen)}
                style={{
                  height: '46px',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  padding: '0 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#252525',
                  boxShadow: showOnInvoiceDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                  outline: showOnInvoiceDropdownOpen ? '1px solid #7c3aed' : 'none'
                }}
              >
                <span>{getYesNoLabel(formData?.showOnInvoice || 'yes')}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{
                    transform: showOnInvoiceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                >
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {showOnInvoiceDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: '#fff',
                    border: '1px solid #eaebf0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    overflow: 'hidden',
                    padding: '4px 0'
                  }}
                >
                  {yesNoOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        onFormChange('showOnInvoice', option.value);
                        trigger('showOnInvoice');
                        setShowOnInvoiceDropdownOpen(false);
                      }}
                      style={{
                        padding: '12px 16px',
                        margin: '0 8px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#252525',
                        backgroundColor: (formData?.showOnInvoice || 'yes') === option.value ? '#f4efff' : '#fff',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if ((formData?.showOnInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = '#f4efff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if ((formData?.showOnInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = '#fff';
                        }
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="form-group">
        <label>{t('UPLOAD_FILE_PDF_1MB')}</label>
        <div className="file-upload-wrapper">
          <div 
            className="file-upload-area"
            onClick={() => document.getElementById('file-upload-materials-pdf')?.click()}
            style={{ cursor: 'pointer' }}
          >
            <button 
              type="button" 
              className="browse-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {selectedFiles.length > 0 ? t('CHANGE_PDF_FILE') : t('UPLOAD_PDF_FILE')}
            </button>
            <input 
              type="file" 
              id="file-upload-materials-pdf" 
              accept={ATTACHMENT_ACCEPT}
              onChange={onFileUpload}
              onClick={(e) => { (e.target as HTMLInputElement).value = ''; }}
              className="file-input"
              style={{ display: 'none' }}
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="uploaded-files">
              {selectedFiles.map((file, index) => (
                <div key={index} className="file-tag">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                    <a
                    href={URL.createObjectURL(file)}
                    download={file.name}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: '#7c3aed',
                    }}
                  >
                    {file.name}
                  </a>
                  <span style={{ marginLeft: '8px', color: '#888', fontSize: '12px' }}>
                    ({(file.size / 1024).toFixed(2)} {t('KB')})
                  </span>
                  <button type="button" className="remove-btn" onClick={() => onRemoveFile(index)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
          {selectedFiles.length === 0 && existingFileName && (
            <div className="uploaded-files">
              <div className="file-tag">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); onDownloadExistingFile?.(); }}
                  style={{ cursor: isDownloadingFile ? 'wait' : 'pointer', textDecoration: 'underline', color: '#7c3aed' }}
                >
                  {existingFileName}
                </a>
                {isDownloadingFile && (
                  <span className="spinner-border spinner-border-sm" style={{ marginLeft: '8px' }} role="status" aria-hidden="true"></span>
                )}
              </div>
            </div>
          )}
        </div>
        {fileError && (
          <div className="error-message" style={{ marginTop: '8px' }}>{fileError}</div>
        )}
      </div>

      {/* Show PDF to Customer - Always Visible */}
       {selectedFiles.length > 0 && (
        <div className="form-group">
          <label>{t('SHOW_PDF_TO_CUSTOMER')}</label>
          <div ref={isPdfAttachedDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isPdfAttachedToInvoice')} />
            <div
              onClick={() => setIsPdfAttachedDropdownOpen(!isPdfAttachedDropdownOpen)}
              style={{
                height: '46px',
                border: '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: '#252525',
                boxShadow: isPdfAttachedDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isPdfAttachedDropdownOpen ? '1px solid #7c3aed' : 'none'
              }}
            >
              <span>{getYesNoLabel(formData?.isPdfAttachedToInvoice || 'yes')}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isPdfAttachedDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isPdfAttachedDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {yesNoOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isPdfAttachedToInvoice', option.value);
                      trigger('isPdfAttachedToInvoice');
                      setIsPdfAttachedDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: (formData?.isPdfAttachedToInvoice || 'yes') === option.value ? '#f4efff' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4efff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Row 2: Date of Purchase & VAT */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('DATE_OF_PURCHASE')}</label>
          <ThemedDatePicker
            value={formData?.dateOfPurchase || ''}
            onChange={(v) => {
              onFormChange('dateOfPurchase', v);
              setValue('dateOfPurchase', v, { shouldValidate: true });
              trigger('dateOfPurchase');
            }}
            hasError={!!errors.dateOfPurchase}
          />
          <input type="hidden" {...register('dateOfPurchase', { required: t('PLEASE_SELECT_PURCHASE_DATE') })} />
          {errors.dateOfPurchase && (
            <div className="error-message">{errors.dateOfPurchase.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('VAT')}</label>
          <div ref={vatDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('vat', { required: t('PLEASE_SELECT_VAT') })} />
            <div
              onClick={() => setVatDropdownOpen(!vatDropdownOpen)}
              style={{
                height: '46px',
                border: errors.vat ? '1px solid #dc3545' : '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: (!formData?.vat || formData?.vat === '') ? '#68727d' : '#252525',
                boxShadow: vatDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: vatDropdownOpen ? '1px solid #7c3aed' : 'none'
              }}
            >
              <span>{getVatLabel(formData?.vat || '')}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: vatDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {vatDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {vatOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('vat', option.value);
                      trigger('vat');
                      setVatDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: formData?.vat === option.value ? '#f4efff' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (formData?.vat !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4efff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData?.vat !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.vat && (
            <div className="error-message">{errors.vat.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 3: Amount (Full Width) */}
      <div className="form-group">
        <label>{t('AMOUNT_SEK_EX_VAT')}</label>
        <input
          type="number"
          min="0"
          {...register('amountExVat', { required: t('PLEASE_ENTER_AMOUNT') })}
          className={`form-control ${errors.amountExVat ? 'error' : ''}`}
          placeholder={t('ENTER_AMOUNT')}
          onKeyDown={(e) => {
            if (e.key === '-' || e.key === 'e' || e.key === 'E') {
              e.preventDefault();
            }
          }}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '' || parseFloat(value) >= 0) {
              onFormChange('amountExVat', value);
              trigger('amountExVat');
            }
          }}
        />
        {errors.amountExVat && (
          <div className="error-message">{errors.amountExVat.message?.toString()}</div>
        )}
      </div>

      {/* Total Section */}
      <div className="summary-section">
        <div className="summary-item">
          <span className="summary-label">{t('TOTAL_INC_VAT')}</span>
          <span className="summary-value">{totalWithVat} {t('SEK')}</span>
        </div>
      </div>
    </>
  );
};

export default MaterialsToolsForm;
